import { useCallback, useEffect, useState } from 'react';
import theme from '../adminTheme';
import AdminSpinner from '../components/AdminSpinner';
import AdminPagination from '../components/AdminPagination';
import { useToast } from '../context/ToastContext';
import {
  extraerMeetId,
  getAtletas,
  getDivisiones,
  getMeet,
  getMeets,
  getDoping,
  generarDoping,
  eliminarMeet,
  sincronizarMeet,
  getLiftingCastDeEvento,
  sincronizarLiftingCastEvento,
} from '../../../services/liftingcastService';

const POR_PAGINA = 50;

const GENEROS = {
  MALE: 'Masculino',
  FEMALE: 'Femenino',
};

const formatearGenero = (valor) => GENEROS[valor] || valor || '—';

const formatearModalidad = (valor) => {
  if (!valor) return '—';
  return valor
    .split('_')
    .map((parte) => parte.charAt(0) + parte.slice(1).toLowerCase())
    .join(' ');
};

const formatearFecha = (meet) => {
  if (!meet) return '—';
  if (meet.fecha) {
    const [anio, mes, dia] = meet.fecha.split('-');
    return `${dia}/${mes}/${anio}`;
  }
  return meet.fecha_texto || '—';
};

const formatearSincronizacion = (valor) => {
  if (!valor) return 'Nunca';
  const fecha = new Date(`${valor}Z`);
  if (Number.isNaN(fecha.getTime())) return valor;
  return fecha.toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' });
};

const formatearLifts = (lifts) => {
  if (!lifts) return '—';
  const activos = [
    lifts.squat && 'SQ',
    lifts.bench && 'BP',
    lifts.dead && 'DL',
  ].filter(Boolean);
  return activos.length ? activos.join(' · ') : '—';
};

const CeldaIntentos = ({ intentos, mejor }) => {
  if (!intentos || !intentos.length) {
    if (mejor != null && mejor !== '') {
      return <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{mejor}</span>;
    }
    return <span style={{ color: '#999' }}>—</span>;
  }
  return (
    <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', fontVariantNumeric: 'tabular-nums' }}>
      {intentos.map((intento) => {
        const fallido = intento.resultado === 'bad';
        const bueno = intento.resultado === 'good';
        const esMejor = bueno && mejor != null && Number(intento.peso) === Number(mejor);
        return (
          <span
            key={intento.n}
            title={`${intento.n}º ${fallido ? 'nulo' : bueno ? 'bueno' : 'sin veredicto'}`}
            style={{
              color: fallido ? '#b91c1c' : bueno ? '#15803d' : '#6b7280',
              textDecoration: fallido ? 'line-through' : 'none',
              fontWeight: esMejor ? 700 : 500,
            }}
          >
            {intento.peso ?? '—'}
            {fallido ? 'x' : ''}
          </span>
        );
      })}
    </span>
  );
};

const estilos = {
  formSync: {
    display: 'flex',
    gap: 12,
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    padding: 16,
    background: '#f8fafc',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    marginBottom: 24,
  },
  ayuda: {
    color: '#666',
    fontSize: 13,
    margin: '8px 0 0 0',
    flexBasis: '100%',
  },
  meetCard: {
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    padding: 16,
    marginBottom: 12,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    flexWrap: 'wrap',
  },
  meetNombre: { margin: 0, fontSize: 17, fontWeight: 600, color: '#111' },
  meetMeta: { color: '#666', fontSize: 13, marginTop: 6 },
  filtros: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
    alignItems: 'center',
    marginBottom: 16,
  },
  filtroSelect: { ...theme.select, width: 'auto', minWidth: 160 },
  filtroInput: { ...theme.searchInput, minWidth: 240 },
  resumen: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
    marginBottom: 20,
  },
  resumenItem: {
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    padding: '10px 14px',
    minWidth: 120,
  },
  resumenValor: { fontSize: 20, fontWeight: 700, color: '#1a4d8c' },
  resumenLabel: { fontSize: 12, color: '#666', textTransform: 'uppercase' },
  tabs: {
    display: 'flex',
    gap: 8,
    borderBottom: '1px solid #e5e7eb',
    marginBottom: 16,
  },
  tab: {
    padding: '10px 16px',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 600,
    color: '#666',
    borderBottom: '2px solid transparent',
  },
  tabActiva: { color: '#1a4d8c', borderBottomColor: '#1a4d8c' },
  aviso: {
    padding: 12,
    background: '#fff7ed',
    border: '1px solid #fed7aa',
    borderRadius: 8,
    color: '#9a3412',
    fontSize: 14,
    marginBottom: 20,
  },
  badgeDev: {
    ...theme.badge,
    background: '#e0e7ff',
    color: '#3730a3',
    textTransform: 'none',
    marginLeft: 10,
    verticalAlign: 'middle',
  },
  btnDoping: {
    padding: '8px 14px',
    background: '#fff7ed',
    color: '#9a3412',
    border: '1px solid #fed7aa',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 600,
    marginRight: 8,
  },
  diasBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
    marginBottom: 16,
  },
  diaCard: {
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    padding: 16,
    marginBottom: 12,
    background: '#f8fafc',
  },
  diaTitulo: { margin: 0, fontSize: 16, fontWeight: 700, color: '#111' },
  diaHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
    marginBottom: 10,
  },
  diaAcciones: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  diaMeta: { color: '#666', fontSize: 13, margin: '10px 0 0' },
  flightsGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  flightChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    border: '1px solid #d1d5db',
    borderRadius: 8,
    background: '#fff',
    cursor: 'pointer',
    fontSize: 13,
  },
  flightChipActiva: {
    borderColor: '#1a4d8c',
    background: '#eff6ff',
    color: '#1a4d8c',
    fontWeight: 600,
  },
};

/**
 * Detalle de una competencia: resumen, nómina de atletas y divisiones.
 */
const DetalleMeet = ({ meetId, onVolver, onSincronizado, onAbrirDoping, onSincronizar }) => {
  const { showToast } = useToast();

  const [meet, setMeet] = useState(null);
  const [tab, setTab] = useState('nomina');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sincronizando, setSincronizando] = useState(false);

  const [atletas, setAtletas] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 0 });
  const [divisiones, setDivisiones] = useState([]);

  const [page, setPage] = useState(1);
  const [buscar, setBuscar] = useState('');
  const [buscarAplicado, setBuscarAplicado] = useState('');
  const [equipo, setEquipo] = useState('');
  const [sesion, setSesion] = useState('');
  const [genero, setGenero] = useState('');

  const cargarMeet = useCallback(async () => {
    try {
      setError(null);
      setMeet(await getMeet(meetId));
    } catch (err) {
      setError(err.message || 'Error al cargar la competencia');
    }
  }, [meetId]);

  const cargarNomina = useCallback(async () => {
    try {
      setError(null);
      const { items, pagination: paginado } = await getAtletas(meetId, {
        page,
        limit: POR_PAGINA,
        buscar: buscarAplicado,
        equipo,
        sesion,
        genero,
      });
      setAtletas(items);
      setPagination(paginado);
    } catch (err) {
      setError(err.message || 'Error al cargar la nómina');
      setAtletas([]);
    }
  }, [meetId, page, buscarAplicado, equipo, sesion, genero]);

  const cargarDivisiones = useCallback(async () => {
    try {
      setError(null);
      setDivisiones(await getDivisiones(meetId));
    } catch (err) {
      setError(err.message || 'Error al cargar las divisiones');
      setDivisiones([]);
    }
  }, [meetId]);

  useEffect(() => {
    let vigente = true;
    setLoading(true);
    Promise.all([cargarMeet(), cargarNomina(), cargarDivisiones()]).finally(() => {
      if (vigente) setLoading(false);
    });
    return () => {
      vigente = false;
    };
    // Solo en el montaje y al cambiar de competencia; los filtros tienen su propio efecto
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetId]);

  useEffect(() => {
    if (loading) return;
    cargarNomina();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, buscarAplicado, equipo, sesion, genero]);

  // Los filtros vuelven al inicio del listado para no quedar en una página vacía
  useEffect(() => {
    setPage(1);
  }, [buscarAplicado, equipo, sesion, genero]);

  const handleSincronizar = async () => {
    setSincronizando(true);
    try {
      const { mensaje } = onSincronizar
        ? await onSincronizar()
        : await sincronizarMeet(meetId);
      showToast(mensaje, 'success');
      await Promise.all([cargarMeet(), cargarNomina(), cargarDivisiones()]);
      onSincronizado?.();
    } catch (err) {
      showToast(err.message || 'No se pudo actualizar la competencia', 'error');
    } finally {
      setSincronizando(false);
    }
  };

  const handleBuscar = (e) => {
    e.preventDefault();
    setBuscarAplicado(buscar.trim());
  };

  if (loading) {
    return (
      <div style={theme.container}>
        <AdminSpinner label="Cargando competencia..." />
      </div>
    );
  }

  return (
    <div style={theme.container}>
      <div style={theme.breadcrumb}>
        <button
          type="button"
          onClick={onVolver}
          style={{ ...theme.breadcrumbLink, border: 'none', background: 'none', padding: 0 }}
        >
          Competencias LiftingCast
        </button>
        <span style={theme.breadcrumbSep}>/</span>
        <span style={theme.breadcrumbCurrent}>{meet?.nombre || meetId}</span>
      </div>

      <div style={theme.pageHeader}>
        <div>
          <h1 style={theme.title}>{meet?.nombre || meetId}</h1>
          <p style={theme.subtitle}>
            Resultados: 3 intentos de squat, banca y peso muerto, total y DOTS.
            {' · '}
            {formatearFecha(meet)}
            {meet?.federacion ? ` · ${meet.federacion}` : ''}
            {meet?.unidades ? ` · ${meet.unidades}` : ''}
            {' · Última actualización: '}
            {formatearSincronizacion(meet?.sincronizado_at)}
          </p>
        </div>
        <div style={theme.btnRow}>
          <a
            href={meet?.url || `https://liftingcast.com/meets/${meetId}/results`}
            target="_blank"
            rel="noreferrer"
            style={{ ...theme.btnSecondary, textDecoration: 'none', display: 'inline-block' }}
          >
            Ver en LiftingCast
          </a>
          <button
            type="button"
            style={theme.btnPrimary}
            onClick={handleSincronizar}
            disabled={sincronizando}
          >
            {sincronizando ? 'Actualizando...' : 'Actualizar datos'}
          </button>
          {onAbrirDoping && (
            <button type="button" style={estilos.btnDoping} onClick={onAbrirDoping}>
              Test doping
            </button>
          )}
        </div>
      </div>

      {error && <div style={theme.error}>{error}</div>}

      {meet && !meet.tiene_resultados && (
        <div style={estilos.aviso}>
          Esta competencia todavía no tiene resultados publicados en LiftingCast: los{' '}
          {meet.total_intentos} intentos están sin peso ni veredicto. Por ahora solo se
          muestra la nómina de inscritos.
        </div>
      )}

      <div style={estilos.resumen}>
        <div style={estilos.resumenItem}>
          <div style={estilos.resumenValor}>{meet?.total_atletas ?? 0}</div>
          <div style={estilos.resumenLabel}>Atletas</div>
        </div>
        <div style={estilos.resumenItem}>
          <div style={estilos.resumenValor}>{meet?.total_divisiones ?? 0}</div>
          <div style={estilos.resumenLabel}>Divisiones</div>
        </div>
        <div style={estilos.resumenItem}>
          <div style={estilos.resumenValor}>{meet?.equipos?.length ?? 0}</div>
          <div style={estilos.resumenLabel}>Equipos</div>
        </div>
        <div style={estilos.resumenItem}>
          <div style={estilos.resumenValor}>{meet?.total_intentos ?? 0}</div>
          <div style={estilos.resumenLabel}>Intentos</div>
        </div>
      </div>

      <div style={estilos.tabs}>
        <button
          type="button"
          style={{ ...estilos.tab, ...(tab === 'nomina' ? estilos.tabActiva : {}) }}
          onClick={() => setTab('nomina')}
        >
          Resultados
        </button>
        <button
          type="button"
          style={{ ...estilos.tab, ...(tab === 'divisiones' ? estilos.tabActiva : {}) }}
          onClick={() => setTab('divisiones')}
        >
          Divisiones
        </button>
      </div>

      {tab === 'nomina' && (
        <>
          <form style={estilos.filtros} onSubmit={handleBuscar}>
            <input
              type="search"
              style={estilos.filtroInput}
              placeholder="Buscar por nombre, equipo o división"
              value={buscar}
              onChange={(e) => setBuscar(e.target.value)}
            />
            <button type="submit" style={theme.btnPrimary}>Buscar</button>

            <select
              style={estilos.filtroSelect}
              value={equipo}
              onChange={(e) => setEquipo(e.target.value)}
              aria-label="Filtrar por equipo"
            >
              <option value="">Todos los equipos</option>
              {(meet?.equipos || []).map((eq) => (
                <option key={eq.nombre} value={eq.nombre === '—' ? '' : eq.nombre}>
                  {eq.nombre} ({eq.total})
                </option>
              ))}
            </select>

            <select
              style={estilos.filtroSelect}
              value={sesion}
              onChange={(e) => setSesion(e.target.value)}
              aria-label="Filtrar por sesión"
            >
              <option value="">Todas las sesiones</option>
              {(meet?.sesiones || []).map((s) => (
                <option key={s} value={s}>Sesión {s}</option>
              ))}
            </select>

            <select
              style={estilos.filtroSelect}
              value={genero}
              onChange={(e) => setGenero(e.target.value)}
              aria-label="Filtrar por género"
            >
              <option value="">Todos</option>
              <option value="MALE">Masculino</option>
              <option value="FEMALE">Femenino</option>
            </select>
          </form>

          {atletas.length === 0 ? (
            <div style={theme.empty}>No hay atletas que coincidan con el filtro.</div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table style={theme.table}>
                  <thead>
                    <tr>
                      <th style={theme.th}>Atleta</th>
                      <th style={theme.th}>SQ</th>
                      <th style={theme.th}>BP</th>
                      <th style={theme.th}>DL</th>
                      <th style={theme.th}>Total</th>
                      <th style={theme.th}>DOTS</th>
                      <th style={theme.th}>Peso</th>
                      <th style={theme.th}>División</th>
                      <th style={theme.th}>Equipo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {atletas.map((a) => (
                      <tr key={a.id}>
                        <td style={theme.td}>
                          {a.nombre || '—'}
                          <div style={{ fontSize: 12, color: '#666' }}>
                            {formatearGenero(a.genero)}
                            {a.sesion ? ` · S${a.sesion}` : ''}
                            {a.flight ? ` · ${a.flight}` : ''}
                          </div>
                        </td>
                        <td style={theme.td}>
                          <CeldaIntentos intentos={a.intentos?.squat} mejor={a.squat} />
                        </td>
                        <td style={theme.td}>
                          <CeldaIntentos intentos={a.intentos?.bench} mejor={a.bench} />
                        </td>
                        <td style={theme.td}>
                          <CeldaIntentos intentos={a.intentos?.deadlift} mejor={a.deadlift} />
                        </td>
                        <td style={{ ...theme.td, fontWeight: 700 }}>{a.total ?? '—'}</td>
                        <td style={theme.td}>{a.dots ?? '—'}</td>
                        <td style={theme.td}>{a.peso_corporal ?? '—'}</td>
                        <td style={theme.td}>
                          {a.division_nombre || '—'}
                          {a.clase_peso ? ` · ${a.clase_peso}` : ''}
                          {a.modalidad ? (
                            <div style={{ fontSize: 12, color: '#666' }}>{formatearModalidad(a.modalidad)}</div>
                          ) : null}
                        </td>
                        <td style={theme.td}>{a.equipo || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <AdminPagination
                page={pagination.page}
                totalPages={pagination.total_pages}
                total={pagination.total}
                pageSize={pagination.limit || POR_PAGINA}
                hasPrev={pagination.has_prev}
                hasNext={pagination.has_next}
                onPrev={() => setPage((p) => Math.max(1, p - 1))}
                onNext={() => setPage((p) => p + 1)}
                label="atletas"
              />
            </>
          )}
        </>
      )}

      {tab === 'divisiones' && (
        divisiones.length === 0 ? (
          <div style={theme.empty}>Esta competencia no tiene divisiones cargadas.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={theme.table}>
              <thead>
                <tr>
                  <th style={theme.th}>División</th>
                  <th style={theme.th}>Código</th>
                  <th style={theme.th}>Género</th>
                  <th style={theme.th}>Modalidad</th>
                  <th style={theme.th}>Levantamientos</th>
                  <th style={theme.th}>Clases de peso</th>
                  <th style={theme.th}>Inscritos</th>
                </tr>
              </thead>
              <tbody>
                {divisiones.map((d) => (
                  <tr key={d.id}>
                    <td style={theme.td}>{d.nombre || '—'}</td>
                    <td style={theme.td}>{d.codigo || '—'}</td>
                    <td style={theme.td}>{formatearGenero(d.genero)}</td>
                    <td style={theme.td}>{formatearModalidad(d.modalidad)}</td>
                    <td style={theme.td}>{formatearLifts(d.lifts)}</td>
                    <td style={theme.td}>{d.clases_peso?.length || 0}</td>
                    <td style={theme.td}>{d.total_atletas || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
};

const MAX_DIAS_DOPING = 7;

const cantidadTesteos = (n) => {
  if (n <= 0) return 0;
  return Math.max(1, Math.floor(n * 0.10 + 0.4));
};

const repartirTesteos = (n) => {
  if (n <= 0) return { fuertes: 0, azar: 0 };
  if (n <= 3) return { fuertes: n, azar: 0 };
  if (n <= 6) return { fuertes: 3, azar: n - 3 };
  return { fuertes: Math.floor((n + 1) / 2), azar: Math.floor(n / 2) };
};

/**
 * Selección de controles de doping para una competencia.
 */
const DopingView = ({ meetId, onVolver }) => {
  const { showToast } = useToast();
  const [meet, setMeet] = useState(null);
  const [preview, setPreview] = useState(null);
  const [doping, setDoping] = useState(null);
  const [flights, setFlights] = useState([]);
  const [nDias, setNDias] = useState(1);
  const [asignacion, setAsignacion] = useState({});
  const [loading, setLoading] = useState(true);
  const [generandoDia, setGenerandoDia] = useState(null);
  const [error, setError] = useState(null);

  const cargar = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getDoping(meetId);
      setMeet(data.meet);
      setPreview(data.preview);
      setDoping(data.doping);
      const grupos = data.flights || [];
      setFlights(grupos);
      const guardada = data.doping?.configuracion?.dias;
      if (Array.isArray(guardada) && guardada.length) {
        setNDias(guardada.length);
      }
    } catch (err) {
      setError(err.message || 'Error al cargar doping');
    } finally {
      setLoading(false);
    }
  }, [meetId]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const cambiarDias = (valor) => {
    const n = Math.max(1, Math.min(MAX_DIAS_DOPING, Number(valor) || 1));
    setNDias(n);
    setAsignacion((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((clave) => {
        if (next[clave] > n) delete next[clave];
      });
      return next;
    });
  };

  const toggleFlight = (clave, dia) => {
    setAsignacion((prev) => {
      if (prev[clave] === dia) {
        const next = { ...prev };
        delete next[clave];
        return next;
      }
      return { ...prev, [clave]: dia };
    });
  };

  const diasPayload = Array.from({ length: nDias }, (_, i) => {
    const dia = i + 1;
    return {
      dia,
      flights: flights
        .filter((g) => asignacion[g.clave] === dia)
        .map((g) => ({ sesion: g.sesion, flight: g.flight })),
    };
  });

  const previewDias = diasPayload.map((dia) => {
    const grupos = flights.filter((g) => asignacion[g.clave] === dia.dia);
    const tested = grupos.reduce((acc, g) => acc + g.tested, 0);
    const total = grupos.reduce((acc, g) => acc + g.total_atletas, 0);
    const pool = tested > 0 ? tested : total;
    const testeos = cantidadTesteos(pool);
    const cupos = repartirTesteos(testeos);
    return {
      dia: dia.dia,
      pool,
      universo: tested > 0 ? 'tested' : 'todos',
      testeos,
      ...cupos,
    };
  });

  const seleccionarTodos = (dia) => {
    setAsignacion((prev) => {
      const next = { ...prev };
      flights.forEach((g) => {
        next[g.clave] = dia;
      });
      return next;
    });
  };

  const deseleccionarTodos = (dia) => {
    setAsignacion((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((clave) => {
        if (next[clave] === dia) delete next[clave];
      });
      return next;
    });
  };

  const handleGenerarDia = async (dia) => {
    const flightsDia = flights
      .filter((g) => asignacion[g.clave] === dia)
      .map((g) => ({ sesion: g.sesion, flight: g.flight }));
    if (!flightsDia.length) {
      showToast(`El día ${dia} no tiene flights. Marca al menos uno.`, 'error');
      return;
    }
    const yaHay = (doping?.seleccion || []).some((a) => (a.dia || 1) === dia);
    if (yaHay) {
      const ok = window.confirm(
        `Esto reemplaza solo la selección del día ${dia} (el sorteo al azar de ese día va a cambiar). ¿Continuar?`
      );
      if (!ok) return;
    }
    setGenerandoDia(dia);
    try {
      const data = await generarDoping(meetId, { dia, flights: flightsDia });
      setDoping(data.doping);
      setMeet(data.meet);
      showToast(data.mensaje, 'success');
    } catch (err) {
      showToast(err.message || 'No se pudo generar la selección', 'error');
    } finally {
      setGenerandoDia(null);
    }
  };

  const cruda = doping?.seleccion || [];
  const seleccion = cruda.some((a) => a.dia) ? cruda.filter((a) => a.dia) : cruda;
  const cupos = doping || preview;
  const totalPreview = previewDias.reduce((acc, d) => acc + d.testeos, 0);

  if (loading) {
    return (
      <div style={theme.container}>
        <AdminSpinner label="Cargando doping..." />
      </div>
    );
  }

  return (
    <div style={theme.container}>
      <div style={theme.breadcrumb}>
        <button
          type="button"
          onClick={onVolver}
          style={{ ...theme.breadcrumbLink, border: 'none', background: 'none', padding: 0 }}
        >
          Competencias LiftingCast
        </button>
        <span style={theme.breadcrumbSep}>/</span>
        <span style={theme.breadcrumbCurrent}>{meet?.nombre || meetId} · Test doping</span>
      </div>

      <div style={theme.pageHeader}>
        <div>
          <h1 style={theme.title}>Test doping</h1>
          <p style={theme.subtitle}>
            {meet?.nombre || meetId}
            {` · ${nDias} día${nDias === 1 ? '' : 's'}`}
            {` · ${totalPreview} testeos previstos (10% por día)`}
            {cupos?.total_testeos ? ` · última generación: ${cupos.total_testeos}` : ''}
          </p>
        </div>
      </div>

      {error && <div style={theme.error}>{error}</div>}

      <div style={estilos.aviso}>
        Elige cuántos días dura la competencia y qué flights entran cada día.
        Los flights empiezan sin marcar. Genera o vuelve a generar cada día por separado.
        El 10% se calcula por día sobre la nómina Tested de esos flights.
      </div>

      <div style={estilos.diasBar}>
        <label htmlFor="doping-dias" style={{ fontWeight: 600 }}>
          Días de competencia
        </label>
        <select
          id="doping-dias"
          style={estilos.filtroSelect}
          value={nDias}
          onChange={(e) => cambiarDias(e.target.value)}
        >
          {Array.from({ length: MAX_DIAS_DOPING }, (_, i) => i + 1).map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
      </div>

      {Array.from({ length: nDias }, (_, i) => i + 1).map((dia) => {
        const prev = previewDias[dia - 1];
        const yaGenerado = seleccion.some((a) => (a.dia || 1) === dia);
        const generandoEste = generandoDia === dia;
        return (
          <div key={dia} style={estilos.diaCard}>
            <div style={estilos.diaHeader}>
              <h2 style={estilos.diaTitulo}>Día {dia}</h2>
              <div style={estilos.diaAcciones}>
                <button
                  type="button"
                  style={theme.btnGhost}
                  onClick={() => seleccionarTodos(dia)}
                  disabled={!flights.length || generandoEste}
                >
                  Seleccionar todos
                </button>
                <button
                  type="button"
                  style={theme.btnGhost}
                  onClick={() => deseleccionarTodos(dia)}
                  disabled={!flights.length || generandoEste}
                >
                  Deseleccionar todos
                </button>
                <button
                  type="button"
                  style={theme.btnPrimary}
                  onClick={() => handleGenerarDia(dia)}
                  disabled={generandoDia != null}
                >
                  {generandoEste
                    ? 'Generando...'
                    : (yaGenerado ? `Volver a generar día ${dia}` : `Generar día ${dia}`)}
                </button>
              </div>
            </div>
            {flights.length === 0 ? (
              <div style={theme.empty}>
                No hay flights en la nómina. Sincroniza de nuevo el meet desde Eventos
                (el link de archive.liftingcast.com también sirve) y vuelve a abrir Test doping.
              </div>
            ) : (
              <div style={estilos.flightsGrid}>
                {flights.map((g) => {
                  const activa = asignacion[g.clave] === dia;
                  const otroDia = asignacion[g.clave] && asignacion[g.clave] !== dia;
                  return (
                    <button
                      key={g.clave}
                      type="button"
                      onClick={() => toggleFlight(g.clave, dia)}
                      style={{
                        ...estilos.flightChip,
                        ...(activa ? estilos.flightChipActiva : {}),
                        opacity: otroDia ? 0.45 : 1,
                      }}
                      title={otroDia ? `Asignado al día ${asignacion[g.clave]}` : g.etiqueta}
                    >
                      <input type="checkbox" readOnly checked={activa} />
                      {g.flight ? `Flight ${g.flight}` : (g.etiqueta || 'Sin flight')}
                      <span style={{ color: '#666', fontWeight: 400 }}>
                        {g.tested || g.total_atletas} {g.tested ? 'Tested' : 'atletas'}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            <p style={estilos.diaMeta}>
              {prev.pool} atletas en el universo · {prev.testeos} testeos
              {prev.testeos ? ` (${prev.fuertes} más fuertes, ${prev.azar} al azar)` : ''}
            </p>
          </div>
        );
      })}

      {preview && preview.n_fuertes > 0 && preview.con_dots === 0 && (
        <div style={estilos.aviso}>
          Todavía no hay resultados (peso corporal y total) en LiftingCast, así que no se puede
          armar el ranking DOTS. Al generar, esos cupos de “más fuertes” pasan al azar hasta que
          existan marcas.
        </div>
      )}

      {!seleccion.length ? (
        <div style={theme.empty}>
          Aún no hay una selección guardada. Generala cuando la nómina y los resultados estén listos.
        </div>
      ) : (
        Array.from({ length: Math.max(nDias, ...seleccion.map((a) => a.dia || 1)) }, (_, i) => i + 1).map((dia) => {
          const filas = seleccion.filter((a) => (a.dia || 1) === dia);
          const fuertes = filas.filter((a) => a.criterio === 'mas_fuerte');
          const azar = filas.filter((a) => a.criterio === 'azar');
          const resumenDia = doping?.configuracion?.por_dia?.find((d) => d.dia === dia);
          return (
            <div key={`sel-${dia}`} style={{ marginTop: 28 }}>
              <h2 style={{ ...estilos.meetNombre, margin: '8px 0 12px' }}>
                Día {dia}
                {resumenDia ? ` · ${resumenDia.total_testeos} testeos` : filas.length ? ` · ${filas.length} testeos` : ''}
              </h2>
              {!filas.length ? (
                <div style={theme.empty}>Sin selección para este día.</div>
              ) : (
                <>
                  <h3 style={{ ...estilos.meetNombre, fontSize: 15, margin: '16px 0 8px' }}>Más fuertes (DOTS)</h3>
                  {fuertes.length === 0 ? (
                    <div style={theme.empty}>No se eligió a nadie por DOTS.</div>
                  ) : (
                    <TablaDoping filas={fuertes} mostrarRanking />
                  )}
                  <h3 style={{ ...estilos.meetNombre, fontSize: 15, margin: '20px 0 8px' }}>Al azar</h3>
                  {azar.length === 0 ? (
                    <div style={theme.empty}>Este cupo va vacío cuando hay 1 a 3 testeos.</div>
                  ) : (
                    <TablaDoping filas={azar} />
                  )}
                </>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

const TablaDoping = ({ filas, mostrarRanking = false }) => (
  <div style={{ overflowX: 'auto' }}>
    <table style={theme.table}>
      <thead>
        <tr>
          {mostrarRanking && <th style={theme.th}># DOTS</th>}
          <th style={theme.th}>Atleta</th>
          <th style={theme.th}>Flight</th>
          <th style={theme.th}>Género</th>
          <th style={theme.th}>Equipo</th>
          <th style={theme.th}>División</th>
          <th style={theme.th}>Peso</th>
          <th style={theme.th}>Total</th>
          <th style={theme.th}>DOTS</th>
        </tr>
      </thead>
      <tbody>
        {filas.map((a) => (
          <tr key={`${a.dia || ''}-${a.lifter_id}`}>
            {mostrarRanking && <td style={theme.td}>{a.ranking_dots || '—'}</td>}
            <td style={theme.td}>{a.nombre || '—'}</td>
            <td style={theme.td}>{a.flight || '—'}</td>
            <td style={theme.td}>{formatearGenero(a.genero)}</td>
            <td style={theme.td}>{a.equipo || '—'}</td>
            <td style={theme.td}>{a.division_nombre || '—'}</td>
            <td style={theme.td}>{a.peso_corporal ?? '—'}</td>
            <td style={theme.td}>{a.total ?? '—'}</td>
            <td style={theme.td}>{a.dots ?? '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/**
 * Listado de competencias sincronizadas desde LiftingCast, con el formulario
 * para traer una nueva a partir de su id o de la URL pública.
 */
const LiftingCastView = ({ eventoId, onBack } = {}) => {
  const { showToast } = useToast();

  const [meets, setMeets] = useState([]);
  const [eventoInfo, setEventoInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [entrada, setEntrada] = useState('');
  const [sincronizando, setSincronizando] = useState(null);
  const [seleccionado, setSeleccionado] = useState(null);
  const [vistaDoping, setVistaDoping] = useState(false);

  const cargarMeets = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      if (eventoId) {
        const { evento, meet } = await getLiftingCastDeEvento(eventoId);
        setEventoInfo(evento);
        setMeets(meet ? [meet] : []);
      } else {
        setEventoInfo(null);
        setMeets(await getMeets());
      }
    } catch (err) {
      setError(err.message || 'Error al cargar las competencias');
      setMeets([]);
    } finally {
      setLoading(false);
    }
  }, [eventoId]);

  useEffect(() => {
    cargarMeets();
  }, [cargarMeets]);

  useEffect(() => {
    if (!eventoId || loading || seleccionado || vistaDoping) return;
    if (meets.length === 1) {
      setSeleccionado(meets[0].meet_id);
    }
  }, [eventoId, loading, meets, seleccionado, vistaDoping]);

  const handleAgregar = async (e) => {
    e.preventDefault();

    const meetId = extraerMeetId(entrada);
    if (!meetId) {
      showToast('Pega la URL del meet en LiftingCast o su id (ej. m7e6fa36l258)', 'error');
      return;
    }

    setSincronizando(meetId);
    try {
      const { mensaje } = await sincronizarMeet(meetId);
      showToast(mensaje, 'success');
      setEntrada('');
      await cargarMeets();
    } catch (err) {
      showToast(err.message || 'No se pudo sincronizar la competencia', 'error');
    } finally {
      setSincronizando(null);
    }
  };

  const handleActualizar = async (meetId) => {
    setSincronizando(meetId);
    try {
      const { mensaje } = eventoId
        ? await sincronizarLiftingCastEvento(eventoId, eventoInfo?.liftingcast_url)
        : await sincronizarMeet(meetId);
      showToast(mensaje, 'success');
      await cargarMeets();
    } catch (err) {
      showToast(err.message || 'No se pudo actualizar la competencia', 'error');
    } finally {
      setSincronizando(null);
    }
  };

  const handleEliminar = async (meet) => {
    const confirmado = window.confirm(
      `¿Eliminar "${meet.nombre || meet.meet_id}" y su nómina de la base de datos? ` +
      'Los datos siguen en LiftingCast y se pueden volver a sincronizar.'
    );
    if (!confirmado) return;

    try {
      await eliminarMeet(meet.meet_id);
      showToast('Competencia eliminada', 'success');
      await cargarMeets();
    } catch (err) {
      showToast(err.message || 'No se pudo eliminar la competencia', 'error');
    }
  };

  if (seleccionado && vistaDoping) {
    return (
      <DopingView
        meetId={seleccionado}
        onVolver={() => {
          setSeleccionado(null);
          setVistaDoping(false);
        }}
      />
    );
  }

  if (seleccionado) {
    return (
      <DetalleMeet
        meetId={seleccionado}
        onVolver={() => {
          if (eventoId && onBack) {
            onBack();
            return;
          }
          setSeleccionado(null);
        }}
        onSincronizado={cargarMeets}
        onAbrirDoping={() => setVistaDoping(true)}
        onSincronizar={eventoId
          ? () => sincronizarLiftingCastEvento(eventoId, eventoInfo?.liftingcast_url)
          : undefined}
      />
    );
  }

  return (
    <div style={theme.container}>
      {onBack && (
        <button type="button" style={{ ...theme.btnGhost, marginBottom: 16 }} onClick={onBack}>
          ← Volver a eventos
        </button>
      )}
      <h1 style={theme.title}>
        {eventoId ? `LiftingCast · ${eventoInfo?.nombre || 'Evento'}` : 'Competencias LiftingCast'}
      </h1>
      <p style={theme.subtitle}>
        {eventoId
          ? 'Nómina de este evento, sincronizada desde LiftingCast. La URL se guarda siempre como /results.'
          : 'Trae la nómina de inscritos de una competencia gestionada en LiftingCast y actualízala cuando cambie en el origen.'}
      </p>

      {!eventoId && (
      <form style={{ ...estilos.formSync, marginTop: 20 }} onSubmit={handleAgregar}>
        <input
          type="text"
          style={{ ...theme.input, flex: 1, minWidth: 280 }}
          placeholder="https://liftingcast.com/meets/m7e6fa36l258/results"
          value={entrada}
          onChange={(e) => setEntrada(e.target.value)}
        />
        <button type="submit" style={theme.btnPrimary} disabled={!!sincronizando}>
          {sincronizando ? 'Sincronizando...' : 'Sincronizar competencia'}
        </button>
        <p style={estilos.ayuda}>
          Acepta /results, archive.liftingcast.com o una URL de plataforma; se guarda siempre como /results.
        </p>
      </form>
      )}

      {error && <div style={theme.error}>{error}</div>}

      {loading ? (
        <AdminSpinner label="Cargando competencias..." />
      ) : meets.length === 0 ? (
        <div style={theme.empty}>
          {eventoId
            ? 'Este evento todavía no tiene una competencia de LiftingCast sincronizada. Vuelve a Eventos, pega el link y pulsa Sincronizar.'
            : 'Todavía no hay competencias sincronizadas. Pega una URL de LiftingCast para empezar.'}
        </div>
      ) : (
        meets.map((meet) => (
          <div key={meet.meet_id} style={estilos.meetCard}>
            <div>
              <h2 style={estilos.meetNombre}>{meet.nombre || meet.meet_id}</h2>
              <div style={estilos.meetMeta}>
                {formatearFecha(meet)}
                {meet.federacion ? ` · ${meet.federacion}` : ''}
                {` · ${meet.total_atletas} atletas · ${meet.total_divisiones} divisiones`}
                {meet.tiene_resultados ? ' · con resultados' : ' · sin resultados aún'}
              </div>
              <div style={estilos.meetMeta}>
                Última actualización: {formatearSincronizacion(meet.sincronizado_at)}
              </div>
            </div>
            <div style={theme.btnRow}>
              <button
                type="button"
                style={theme.btnSecondary}
                onClick={() => {
                  setVistaDoping(false);
                  setSeleccionado(meet.meet_id);
                }}
              >
                Ver nómina
              </button>
              <button
                type="button"
                style={estilos.btnDoping}
                onClick={() => {
                  setVistaDoping(true);
                  setSeleccionado(meet.meet_id);
                }}
              >
                Test doping
                {meet.doping?.total_testeos ? ` (${meet.doping.total_testeos})` : ''}
              </button>
              <button
                type="button"
                style={theme.btnPrimary}
                onClick={() => handleActualizar(meet.meet_id)}
                disabled={sincronizando === meet.meet_id}
              >
                {sincronizando === meet.meet_id ? 'Actualizando...' : 'Actualizar datos'}
              </button>
              <button
                type="button"
                style={theme.btnDanger}
                onClick={() => handleEliminar(meet)}
              >
                Eliminar
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default LiftingCastView;
